"use client";

import classNames from "classnames";
import {
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle,
    useRef,
    ReactElement,
    cloneElement,
    useCallback,
} from "react";
import { FadeInAnimationVariantType } from "@finbackoffice/site-core";
import styles from "./fade-in-animation.module.sass";

export interface IFadeInAnimationProps {
    children: ReactElement<any>;
    variant?: FadeInAnimationVariantType;
    height?: number;
    scopedClassnames?: { variant: string; visible: string; notVisible: string };
    onAnimationEnd?: (targert: HTMLElement, visible: boolean) => void;
}

type FadeInAnimationHandle = {
    clearVisible: () => Promise<void>;
    forceOpen: () => void;
};

const FadeInAnimation = forwardRef<FadeInAnimationHandle, IFadeInAnimationProps>(
    ({ children, variant = "opacity", height, scopedClassnames, onAnimationEnd }, ref) => {
        const [visible, setVisible] = useState(false);
        const timeoutRef = useRef<NodeJS.Timeout | null>(null);
        const animationId = useRef<number>(undefined);

        useEffect(() => {
            animationId.current = requestAnimationFrame(() => {
                setVisible(true);
            });

            return () => {
                if (animationId.current) {
                    cancelAnimationFrame(animationId.current);
                    animationId.current = undefined;
                }
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
            };
        }, []);

        useImperativeHandle(
            ref,
            () => ({
                clearVisible: () => {
                    setVisible(false);
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                        timeoutRef.current = null;
                    }
                    return new Promise<void>((resolve) => {
                        timeoutRef.current = setTimeout(() => {
                            resolve();
                        }, 700);
                    });
                },
                forceOpen: () => {
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                        timeoutRef.current = null;
                    }
                    setVisible(true);
                },
            }),
            [],
        );

        const handleTransitionEnd = useCallback(
            (e: React.TransitionEvent) => {
                const target = e.target as HTMLElement;
                onAnimationEnd?.(target, visible);
            },
            [onAnimationEnd, visible],
        );

        const heightStyles =
            variant === "height" && height
                ? visible
                    ? { height: visible ? height : 0 }
                    : undefined
                : undefined;

        return cloneElement(children, {
            style: { ...children.props.style, ...heightStyles },
            className: classNames(
                children?.props?.className,
                scopedClassnames ? scopedClassnames.notVisible : styles["not-visible"],
                visible && (scopedClassnames ? scopedClassnames.visible : styles.visible),
                scopedClassnames ? scopedClassnames.variant : styles[variant],
            ),
            onTransitionEnd: onAnimationEnd ? handleTransitionEnd : undefined,
        });
    },
);

export default FadeInAnimation;
